import type { FC } from 'react'

import { Skeleton } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import orderBy from 'lodash/orderBy'

import { NoResults } from '@/components/NoResults'
import { PolicyProductCard } from '@/components/policy/ProductCard'
import { normalizeProductData } from '@/components/policy/ProductCard/helper'
import { useRenderLimit } from '@/hooks/use-render-limit'
import { useGroupedProductList, usePolicyId } from '@/modules/ecom-policy/policy/hooks'
import { usePolicyFilters } from '@/modules/ecom-policy/policy/Policy.context'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import type { SortOrder } from '@/modules/ecom-policy/types'

import './products.scss'

export const Products: FC = () => {
  const policyId = usePolicyId()

  const { data: policy } = useQuery(queries.policyDetails(policyId), () =>
    ecomPolicyResources.getPolicyDetails(policyId)
  )
  const { isLoading, isRefetching } = useQuery(
    queries.policyProducts(policyId),
    () => {
      if (!policy) throw Error('Policy data is missing!')
      return ecomPolicyResources.getPolicyProducts(policyId, policy.vendorId)
    },
    { enabled: !!policy }
  )

  const filters = usePolicyFilters()
  const [sortOrder] = filters.sortOrder
  const [sortParam] = filters.sortParam

  const sortedInventoryList = getSortedProductList({ sortOrder, sortParam })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedInventoryList, 30, 30)
  const tenElements = new Array(10)
  tenElements.fill(null)

  if (isLoading || isRefetching) {
    return (
      <div className="grid-cols-fill-15 grid gap-4">
        {tenElements.map((_, index) => (
          <button
            className="relative flex cursor-pointer flex-col items-center justify-between rounded-lg border-2 border-solid border-transparent bg-transparent p-0 transition duration-200 ease-out"
            key={index}
            style={{ pointerEvents: 'none' }}
          >
            <Skeleton height="216px" width={250} />
          </button>
        ))}
      </div>
    )
  }

  if (sortedInventoryList.length === 0) return <NoResults />

  return (
    <div className="grid-cols-fill-15 grid gap-4">
      {/* Show grouped products */}
      {visibleList.map(group => {
        const normalizedData = normalizeProductData({ group })
        return (
          <PolicyProductCard
            key={group.groupedProducts.length === 1 ? group.groupedProducts[0].id : group.groupKey}
            {...normalizedData}
          />
        )
      })}

      <ListLoaderComponent />
    </div>
  )
}

function getSortedProductList({
  sortParam,
  sortOrder,
}: {
  sortOrder: SortOrder
  sortParam: 'priceFrom' | 'isSelected'
}) {
  const list = useGroupedProductList().groupedAndFiltered
  // When opening a policy, the default sorting should be selected products at the top, but the remaining products should be sorted by price, descending
  if (sortParam === 'isSelected') {
    return orderBy(list, ['isSelected', 'priceFrom'], ['desc', 'desc'])
  }
  return orderBy(list, sortParam, sortOrder)
}
